import * as THREE from 'three';
import type { Military, Soldier } from './military';
import type { Aircraft, FlightLeg } from './aviation';
import type { Vec } from '../destruction/structure';
import { Paratroopers } from './paratroopers';
import { Spatial } from '../core/spatial';

const v = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
/** A permanent aircraft and fourteen original roster members start at the
 * actual base strip. Only reset establishes their initial occupied positions.
 * Two operators stay aboard; the other twelve may deploy on a real flight. */
export class MilitaryAviation {
  aircraft?: Aircraft;
  parachutes?: Paratroopers;
  crew: Soldier[] = [];
  phase: 'standby' | 'flying' | 'returning' | 'unavailable' = 'unavailable';
  sorties = 0;
  reason = 'Base aviation unavailable';
  private ready = 35;
  private drop = false;
  private landingSlots = new Map<number, { id: string; until: number; p: THREE.Vector3 }>();
  private landingIndex = new Spatial<{ id: string; until: number; p: THREE.Vector3 }>(2);
  constructor(readonly military: Military) {}
  clear(): void {
    this.parachutes?.clear();
    this.aircraft = undefined;
    this.crew = [];
    this.phase = 'unavailable';
    this.sorties = 0;
    this.ready = 35;
    this.drop = false;
    this.landingSlots.clear();
    this.landingIndex.clear();
  }
  prepare(): void {
    const { population, city } = this.military,
      aviation = population.aviation,
      strip = city.strategic?.transport?.runway;
    if (!aviation || !strip) return;
    this.parachutes ??= new Paratroopers(this.military.physics, population);
    this.parachutes.onLand = (s) => {
      if (s.faction === 'friendly' && !this.military.mobilised) {
        s.assignment = undefined;
        s.state = 'base';
        s.task = '';
        s.retry = 0;
      }
    };
    aviation.troops.onJump = (soldier, _landing, velocity, clearance, carrier) => {
      const landing = this.landing(soldier.person.p, velocity, soldier.id);
      return !!landing && this.parachutes!.launch(soldier, landing, velocity, clearance, carrier);
    };
    this.aircraft = aviation.createMission('garrison-air-service', 'friendly', strip.stand, {
      x: 1,
      y: 0,
      z: 0,
    });
    this.phase = 'standby';
    this.reason = 'Original air team preparing its coastal observation sortie';
  }
  initialPoint(index: number): THREE.Vector3 | undefined {
    const a = this.aircraft,
      local = index - 500;
    if (!a || local < 0 || local >= 14) return;
    return this.military.population.aviation!.troops.point(
      a,
      this.military.population.aviation!.troops.slot(local < 2 ? local : 86 + local),
    );
  }
  assign(soldier: Soldier): boolean {
    const a = this.aircraft,
      index = soldier.index - 500;
    if (!a || index < 0 || index >= 14) return false;
    const slot = index < 2 ? index : 86 + index;
    if (!this.military.population.aviation!.troops.attach(a, soldier, slot))
      throw new Error(`Unsupported original air assignment ${soldier.id}`);
    soldier.assignment = 'air';
    soldier.role = index < 2 ? 'flight operator' : 'airborne relief';
    this.crew.push(soldier);
    if (this.crew.length === 14) a.pilots = this.crew.slice(0, 2).map((s) => s.id);
    return true;
  }
  landing(point: Vec, velocity: Vec, id?: string): Vec | undefined {
    const { population, physics } = this.military,
      network = population.civil?.network;
    if (!network) return;
    const projected = { x: point.x + velocity.x, y: 0, z: point.z + velocity.z };
    let expired = false;
    for (const [key, slot] of this.landingSlots)
      if (slot.until < this.military.time) {
        this.landingSlots.delete(key);
        expired = true;
      }
    if (expired) this.landingIndex.rebuild([...this.landingSlots.values()]);
    const node = network
      .near(projected, 75, 'street')
      .filter((n) => n.y < 12 && !this.landingSlots.has(n.id))
      .sort(
        (a, b) =>
          (a.x - projected.x) ** 2 +
          (a.z - projected.z) ** 2 -
          ((b.x - projected.x) ** 2 + (b.z - projected.z) ** 2),
      )
      .slice(0, 24)
      .find((n) => {
        let reserved = false;
        this.landingIndex.query(n, 0.85, () => {
          reserved = true;
        });
        if (reserved) return false;
        if (
          population.hazardAt(n) ||
          !physics.ground(n, 0.15, 0.25) ||
          physics.blocked(n.x, n.z, 0.3, n.y + 0.85)
        )
          return false;
        let occupied = false;
        population.actorsNear(n, 0.7, (a) => {
          if (a.category === 'person' && !a.hidden && Math.abs(a.p.y - n.y) < 0.8) occupied = true;
        });
        return !occupied;
      });
    if (node && id) {
      const slot = { id, until: this.military.time + 120, p: new THREE.Vector3(node.x, node.y, node.z) };
      this.landingSlots.set(node.id, slot);
      this.landingIndex.update(slot);
    }
    return node;
  }
  private flight(): FlightLeg[] {
    const y = this.military.city.strategic!.transport!.runway.end.y;
    return [
      { point: v(-725, y, 365), speed: 26, state: 'takeoff' },
      { point: v(-570, 48, 365), speed: 42, state: 'takeoff' },
      { point: v(-560, 120, 530), speed: 38, state: 'excursion' },
      { point: v(620, 120, 530), speed: 35, state: 'excursion' },
      { point: v(1180, 260, 760), speed: 48, state: 'excursion' },
      { point: v(1280, 600, -950), speed: 55, state: 'excursion' },
      { point: v(-1280, 600, -950), speed: 55, state: 'excursion' },
      { point: v(-1200, 180, 365), speed: 48, state: 'approach' },
      ...this.arrival(),
    ];
  }
  private arrival(): FlightLeg[] {
    const strip = this.military.city.strategic!.transport!.runway;
    return [
      { point: v(-1120, 50, 365), speed: 32, state: 'approach' },
      { point: v(-1040, 12, 365), speed: 26, state: 'landing' },
      // Level the actual long airframe before the final shallow descent. The
      // old steep coastal approach dipped its forward gear into the water.
      { point: v(-1010, 12, 365), speed: 24, state: 'landing' },
      { point: v(-925, strip.start.y, 365), speed: 22, state: 'landing' },
      { point: v(strip.stand.x, strip.stand.y, 365), speed: 12, state: 'taxi-in' },
    ];
  }
  mobilise(): void {
    this.ready = 0;
    this.drop = false;
    this.reason = 'Existing aircrew preparing airborne relief from their current flight or base';
  }
  standDown(): void {
    const a = this.aircraft;
    this.military.population.aviation?.troops.cancel(a);
    this.parachutes?.cancel();
    this.drop = false;
    for (const s of this.crew)
      if (!s.person.carrier && s.state !== 'airborne') {
        s.assignment = undefined;
      }
    if (a && a.state !== 'wreck' && a.p.y > 20) {
      // Complete the safe current circuit and land through the real strip.
      this.phase = 'returning';
      this.reason = 'Original aircrew returning through the base approach';
    }
  }
  update(dt: number): void {
    this.parachutes?.update(dt);
    const a = this.aircraft,
      { population, physics } = this.military,
      aviation = population.aviation;
    if (!a || !aviation) return;
    if (a.state === 'wreck') {
      this.phase = 'unavailable';
      this.reason = 'Original airframe wreck retained';
      return;
    }
    if (population.airstrikes?.busy(a)) return;
    if (this.phase === 'standby') {
      this.ready -= dt;
      if (this.ready > 0) return;
      const strip = this.military.city.strategic!.transport!.runway;
      if (strip.supports.some((id) => !physics.cellOwner.has(id))) {
        this.reason = 'Damaged base runway; sortie suspended';
        this.ready = 8;
        return;
      }
      if (aviation.routeMission(a, this.flight(), 'Original green air transport on coastal observation')) {
        this.phase = 'flying';
        this.sorties++;
        this.drop = false;
      }
    }
    if (
      this.military.mobilised &&
      this.phase === 'flying' &&
      !this.drop &&
      a.p.y > 80 &&
      a.p.z > 450 &&
      a.p.x < 0
    ) {
      const destinations = new Map(
        this.crew
          .slice(2)
          .filter((s) => s.person.carrier === a.id && !s.person.fatal)
          .map((s) => [s.id, { x: s.person.p.x, y: 0, z: s.person.p.z }]),
      );
      if (destinations.size) this.drop = aviation.troops.beginDrop(a, destinations);
    }
    if ((this.phase === 'flying' || this.phase === 'returning') && !a.route.length && a.p.y < 2) {
      this.phase = 'standby';
      this.ready = 90;
      this.reason = 'Original aircraft back at the base stand';
    }
  }
  render(alpha: number): void {
    this.parachutes?.render(alpha);
  }
  dispose(): void {
    this.parachutes?.dispose();
    this.parachutes = undefined;
  }
  inspect(): unknown {
    return {
      phase: this.phase,
      reason: this.reason,
      sorties: this.sorties,
      aircraft: this.aircraft?.id,
      crew: this.crew.map((s) => ({
        id: s.id,
        state: s.state,
        role: s.role,
        position: s.person.p.toArray(),
      })),
      landed: this.parachutes?.landed,
      lost: this.parachutes?.lost,
      descentLosses: this.parachutes?.losses,
    };
  }
}
